import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import DatePicker from "react-datepicker";
import { Link } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import { IoIosSearch } from "react-icons/io";
import { LuRefreshCw } from "react-icons/lu";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { FaUpload } from "react-icons/fa";
import LoadingSpinner from "../../components/LoadingSpinner";

const ResearchNotification = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [filters, setFilters] = useState({
    title: "",
    message: "",
    screated_on: "",
    ecreated_on: "",
    orderbycol: "created_on",
    orderby: "DESC",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    title: "",
    message: "",
    screated_on: "",
    ecreated_on: "",
    orderbycol: "created_on",
    orderby: "DESC",
  });
  const [dateRange, setDateRange] = useState([null, null]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    showingRecords: "Showing 0 to 0 of 0 entries",
    limit: 100,
  });
  const toastShownRef = useRef(false);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      // Format dates for API request
      const params = {
        ...appliedFilters,
        page: pagination.currentPage,
        limit: pagination.limit,
      };

      // Remove empty date values
      if (!params.screated_on) delete params.screated_on;
      if (!params.ecreated_on) delete params.ecreated_on;

      const response = await axios.get(`${API_URL}PushNotification/research`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && Array.isArray(response.data.data)) {
        const data = response.data.data;
        const totalItems = response.data.total || 0;
        const totalPages = Math.ceil(totalItems / pagination.limit);

        const from = (pagination.currentPage - 1) * pagination.limit + 1;
        const to = Math.min(
          pagination.currentPage * pagination.limit,
          totalItems
        );

        setNotifications(data);
        setPagination((prev) => ({
          ...prev,
          totalPages,
          totalItems,
          showingRecords: `Showing ${from} to ${to} of ${totalItems} entries`,
        }));

        if (data.length > 0 && !toastShownRef.current) {
          showToast.success("Research notifications fetched successfully");
          toastShownRef.current = true;
        }
      } else {
        setNotifications([]);
        setPagination((prev) => ({
          ...prev,
          showingRecords: "Showing 0 to 0 of 0 entries",
          totalPages: 1,
          currentPage: 1,
        }));
      }
    } catch (error) {
      console.error("Error fetching research notifications:", error);
      setNotifications([]);
      showToast.error("Failed to fetch research notifications");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setDateRange(dates);

    // Format dates to YYYY-MM-DD format (without time) for API compatibility
    setFilters((prev) => ({
      ...prev,
      screated_on: start ? start.toISOString().split("T")[0] : "",
      ecreated_on: end ? end.toISOString().split("T")[0] : "",
    }));
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    const defaultFilterState = {
      title: "",
      message: "",
      screated_on: "",
      ecreated_on: "",
      orderbycol: "created_on",
      orderby: "DESC",
    };

    setFilters(defaultFilterState);
    setAppliedFilters(defaultFilterState);
    setDateRange([null, null]);
  };

  const handleClearDate = () => {
    setDateRange([null, null]);
    setFilters((prev) => ({
      ...prev,
      screated_on: "",
      ecreated_on: "",
    }));
  };

  const handleSort = (column) => {
    let sortOrder = "DESC";
    if (column === appliedFilters.orderbycol) {
      sortOrder = appliedFilters.orderby === "DESC" ? "ASC" : "DESC";
    }

    const newFilters = {
      ...appliedFilters,
      orderbycol: column,
      orderby: sortOrder,
    };

    setAppliedFilters(newFilters);
    setFilters(newFilters);
  };

  const handlePageChange = (direction) => {
    setPagination((prev) => {
      let newPage = prev.currentPage;
      if (direction === "prev" && prev.currentPage > 1) {
        newPage -= 1;
      } else if (direction === "next" && prev.currentPage < prev.totalPages) {
        newPage += 1;
      }
      return { ...prev, currentPage: newPage };
    });
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem("authToken");

      const { page, limit, ...restFilters } = appliedFilters;

      // Format dates for export
      const exportParams = {};
      Object.keys(restFilters).forEach((key) => {
        if (restFilters[key] !== "") {
          exportParams[key] = restFilters[key];
        }
      });

      const queryString = new URLSearchParams(exportParams).toString();
      const exportUrl = `${API_URL}PushNotification/research/export?${queryString}`;

      const response = await fetch(exportUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const contentDisposition = response.headers.get("Content-Disposition");
        let filename = `research-notifications-${new Date().toISOString().split("T")[0]
          }.xlsx`;

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showToast.success("Export completed successfully");
      } else {
        throw new Error(`Export failed with status: ${response.status}`);
      }
    } catch (error) {
      console.error("Export error:", error);
      showToast.error("Failed to export research notifications");
    } finally {
      setExportLoading(false);
    }
  };

  const getSortIcon = (column) => {
    if (appliedFilters.orderbycol !== column) return "sort";
    return appliedFilters.orderby === "ASC" ? "sort-down" : "sort-up";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB") + " " + date.toLocaleTimeString();
  };

  useEffect(() => {
    fetchNotifications();
  }, [appliedFilters, pagination.currentPage]);

  return (
    <div className="page-container mt-5">
      <div className="row pb-2">
        <div className="col-md-3"></div>
        <div className="col-md-9 d-flex justify-content-end align-items-center">
          <span className="me-3" style={{ fontSize: "0.8rem" }}>
            {pagination.showingRecords}
          </span>
          <button
            className="btn btn-default btn-sm me-2"
            onClick={() => fetchNotifications()}
          >
            <LuRefreshCw />
          </button>
          <button
            className="btn btn-default btn-sm me-2"
            onClick={handleExport}
            disabled={exportLoading}
          >
            {exportLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Exporting...
              </>
            ) : (
              <>
                <FaUpload /> Export
              </>
            )}
          </button>
          <button
            className="btn btn-default btn-sm me-2"
            onClick={() => handlePageChange("prev")}
            disabled={pagination.currentPage === 1}
            title="Previous"
          >
            <IoIosArrowBack />
          </button>
          <button
            className="btn btn-default btn-sm"
            onClick={() => handlePageChange("next")}
            disabled={pagination.currentPage === pagination.totalPages}
            title="Next"
          >
            <IoIosArrowForward />
          </button>
        </div>
      </div>

      <div className="panel panel-default with-rounded-corner with-shadow p-10">
        <div className="panel-heading">
          <span className="panel-title">Research Notification</span>
        </div>

        <div className="table-responsive" style={{ overflowX: "auto" }}>
          <table
            className="table table-condensed table-hover bg-white"
            style={{ width: "100%", tableLayout: "fixed" }}
          >
            <colgroup>
              <col style={{ width: "30%" }} />
              <col style={{ width: "45%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "5%" }} />
            </colgroup>
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("title")}
                  style={{ cursor: "pointer" }}
                >
                  Notification Title{" "}
                  <i className={`fa fa-${getSortIcon("title")}`}></i>
                </th>
                <th
                  onClick={() => handleSort("message")}
                  style={{ cursor: "pointer" }}
                >
                  Description{" "}
                  <i className={`fa fa-${getSortIcon("message")}`}></i>
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("created_on")}
                >
                  Date Time{" "}
                  <i className={`fa fa-${getSortIcon("created_on")}`}></i>
                </th>
                <th>Action</th>
              </tr>
              <tr>
                <th>
                  <input
                    type="text"
                    name="title"
                    value={filters.title}
                    onChange={handleFilterChange}
                    className="form-control form-control-sm"
                    placeholder="Filter title"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    name="message"
                    value={filters.message}
                    onChange={handleFilterChange}
                    className="form-control form-control-sm"
                    placeholder="Filter message"
                  />
                </th>
                <th>
                  <div className="d-flex align-items-center">
                    <DatePicker
                      selectsRange
                      startDate={dateRange[0]}
                      endDate={dateRange[1]}
                      onChange={handleDateChange}
                      isClearable={true}
                      placeholderText="Filter date range"
                      className="form-control form-control-sm me-2"
                      showTimeSelect={false}
                      dateFormat="dd/MM/yyyy"
                      monthsShown={2}
                      popperPlacement="bottom-start"
                    />
                    {dateRange[0] && (
                      <button
                        type="button"
                        className="btn btn-default btn-sm me-2"
                        onClick={handleClearDate}
                      >
                        Clear
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-default btn-sm ms-2"
                      onClick={handleSearch}
                    >
                      <IoIosSearch />
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger ms-2"
                      style={{
                        padding: "2px 6px",
                        fontSize: "12px",
                        lineHeight: "2",
                      }}
                      onClick={handleClearFilters}
                    >
                      Clear
                    </button>
                  </div>
                </th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                // Skeleton loading rows
                Array.from({ length: 10 }).map((_, skeletonIndex) => (
                  <tr key={`skeleton-${skeletonIndex}`} className="skeleton-row">
                    {Array.from({ length: 4 }).map((_, colIndex) => (
                      <td key={colIndex}>
                        <div
                          className="skeleton-text"
                          style={{
                            width: "80%",
                            margin: "0 auto"
                          }}
                        ></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : notifications.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-3">
                    <p className="fw-bold">No Records Found</p>
                  </td>
                </tr>
              ) : (
                notifications.map((notification) => (
                  <tr key={notification.id}>
                    <td
                      style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                    >
                      {notification.title}
                    </td>
                    <td
                      style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                    >
                      {notification.message}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {formatDate(notification.created_on)}
                    </td>
                    <td>
                      <Link
                        to={`/Campaign/ResearchNotification/view/${notification.id}`}
                        className="text-primary"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div id="myModal" className="modal">
        <div className="modal-dialog panel panel-default panel-border top">
          <div className="modal-content">
            <div id="myModalContent"></div>
          </div>
        </div>
      </div>

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default ResearchNotification;
